import Cookie from '@carvana/stc-cookie';

declare global {
  interface WURFL {
    /* eslint-disable camelcase */
    form_factor: string;
    complete_device_name: string;
    /* eslint-enable */
  }
  interface Window {
    WURFL: WURFL;
  }
}

type BrowserData = {
  referrerUrl: string;
  url: string;
  browserName: string;
  osName: string;
  deviceCategory: string | null;
  deviceName: string | null;
  language: string;
  screenResolution: string;
  browserResolution: string;
};

const getOsName = () => {
  const appVersion = window?.navigator?.appVersion || '';

  if (appVersion.indexOf('Win') !== -1) {
    return 'Windows';
  }
  if (appVersion.indexOf('iPhone OS') !== -1) {
    return 'iPhone OS';
  }
  if (appVersion.indexOf('Mac') !== -1) {
    return 'MacOS';
  }
  if (appVersion.indexOf('X11') !== -1) {
    return 'UNIX';
  }
  if (appVersion.indexOf('Linux') !== -1) {
    return 'Linux';
  }
  return `${window?.navigator?.platform || 'Unknown OS'}`;
};

const getBrowserName = () => {
  const useragent = window.navigator.userAgent;
  let matchedName =
    useragent.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
  if (/trident/i.test(matchedName[1])) {
    const ieRevision = /\brv[ :]+(\d+)/g.exec(useragent) || [];
    return `IE ${ieRevision[1] || ''}`;
  }
  if (matchedName[1] === 'Chrome') {
    const operaRevision = useragent.match(/\bOPR\/(\d+)/);
    if (operaRevision !== null) {
      return `Opera ${operaRevision[1]}`;
    }
    const edgeRevision = useragent.match(/\bEdg\/(\d+)/);
    if (edgeRevision !== null) {
      return `Edge ${edgeRevision[1]}`;
    }
  }
  matchedName = matchedName[2]
    ? [matchedName[1], matchedName[2]]
    : [window.navigator.appName, window.navigator.appVersion, '-?'];
  const revision = useragent.match(/version\/(\d+)/i);
  if (revision !== null) {
    matchedName.splice(1, 1, revision[1]);
  }

  return matchedName.join(' ');
};

const getIpAddress = () => Cookie.getCookie('CVCurrentIpAddress') || '';

const getBrowserCookieId = () => Cookie.getBcId() || '';

type StaticBrowserData = {
  browserName: string;
  osName: string;
  screenResolution: string;
  deviceCategory: string | null;
  deviceName: string | null;
  language: string;
};

// Memoized static browser data that doesn't change during a session
const getStaticBrowserData = (() => {
  let cachedData: StaticBrowserData | null = null;

  const getData = () => {
    if (!cachedData) {
      cachedData = {
        browserName: getBrowserName(),
        osName: getOsName(),
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        /* eslint-disable camelcase */
        deviceCategory: window.WURFL?.complete_device_name,
        deviceName: window.WURFL?.form_factor,
        /* eslint-enable */
        language: window.navigator.language
      };
    }
    return cachedData;
  };

  // For testing purposes - allows resetting the cache
  getData.resetCache = () => {
    cachedData = null;
  };

  return getData;
})();

const getBrowserData = (): BrowserData => {
  const staticData = getStaticBrowserData();

  return {
    ...staticData,
    browserResolution: `${window.innerWidth}x${window.innerHeight}`,
    referrerUrl: window.document.referrer,
    url: window.location.href
  };
};

// Export resetCache function for testing purposes
const resetBrowserDataCache = () => getStaticBrowserData.resetCache();

export { BrowserData, getBrowserData, getIpAddress, resetBrowserDataCache, getBrowserCookieId };
